import { Draggable, MbscCalendarEvent } from "@mobiscroll/react";
import { Badge, Box, Grid2, IconButton, ListItemText, Menu, MenuItem, Skeleton, Tooltip, Typography } from "@mui/material";
import { MouseEvent, useCallback, useState } from "react";
import ProviderTypeChip from "./ProviderTypeChip";
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import { gql, useMutation, useQuery } from "@apollo/client";
import { useSnackbar } from "~/hooks/useSnackbar";
import { startCase, uniq } from "lodash";
import { CustomMbscCalendarEventData, LocalVisitRequest } from "~/utils/eventsHelper";
import { addMinutes } from "date-fns";
import { CommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import { COMMAND_INDEX_PROVIDERS_QUERY } from "~/queries/forms";

interface VisitRequestSlotProps {
  data: LocalVisitRequest;
  isDraggable?: boolean;
  isPatientView?: boolean;
}

const VisitRequetSlot: React.FC<VisitRequestSlotProps> = ({ data, isDraggable, isPatientView = false }) => {
  const [draggable, setDraggable] = useState<HTMLElement | null>(null);
  const [cancelingVisitRequest, setCancelingVisitRequest] = useState(false);

  const { data: providerData } = useQuery<CommandIndexProvidersQuery>(COMMAND_INDEX_PROVIDERS_QUERY, {
    variables: {
      active: true
    }
  });

  const patient = data?.patient;

  const { setErrorMessage } = useSnackbar();

  const setDragElm = useCallback((elm: HTMLElement | null) => {
    setDraggable(elm);
  }, []);

  const [cancelVisitRequest] = useMutation(CANCEL_VISIT_REQUEST_TASK,
    {
      refetchQueries: ['TaskSidebar'],
      onError: () => {
        setErrorMessage(["Something went wrong when trying to remove visit."])
        setCancelingVisitRequest(false);
      }
    }
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const backgroundColor = 'white';

  const visitType = data?.visitType

  const isMultiResource = visitType?.visitResourceRequirements?.length > 1;

  const visitResourceRequirement = isMultiResource ? visitType?.visitResourceRequirements?.find((vrr) => vrr.inHome) : visitType?.visitResourceRequirements[0];

  const providerType = visitResourceRequirement?.providerTypes[0];

  const providerTypeIds = uniq(visitType?.visitResourceRequirements?.filter((vrr) => vrr.requiresContinuityOfCare)?.flatMap((vrr) => vrr.providerTypes.map((pt) => pt.id)));

  const patientCareTeamData = patient?.careTeamProviders
    .filter((coc) => providerTypeIds?.includes(coc.providerTypeId)).map((coc) => {
      return {
        provider: providerData?.commandIndexProviders?.providers?.find((p: CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]) => p.id === coc.providerId),
      }
    });

  const content = (
    <Grid2 container flexDirection="row" alignItems="center" position="relative" sx={{ width: '100%' }}>
      <Box flexDirection="column" sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignSelf: 'flex-end' }}>
        <Typography variant="subtitle2">{`${visitType?.name}`}</Typography>
        <ProviderTypeChip
          providerTypeName={`${providerType?.name}`}
          sx={{ maxWidth: 120 }}
          disableTooltip={isPatientView}
        />
        <Tooltip placement="top" title={patient?.patientNotes}>
          <Typography sx={{ display: 'block' }} variant="caption">{`${patient?.firstName} ${patient?.lastName}`}</Typography>
        </Tooltip>
        
        {isDraggable && <Draggable dragData={createVisitRequestEventFromRequest(data) as unknown as MbscCalendarEvent} element={draggable} />}
      </Box>
      <Box sx={{ position: 'absolute', right: "-6px", bottom: 0, display: 'flex', alignItems: 'center' }}>
      {isMultiResource && (
        <Tooltip placement="top" title={`Needs: ${visitType.visitResourceRequirements.map((vrr) => startCase(vrr?.providerTypes?.map((pt) => pt.name).join(" or "))).join(", ")}`}>
          <PeopleAltIcon sx={{ width: 23, height: 23 }} />
        </Tooltip>
      )}
      {patientCareTeamData && patientCareTeamData?.length > 0 && (
        <Tooltip placement="top" title={`Care team: ${patientCareTeamData?.map((coc) => `${coc.provider?.firstName} ${coc.provider?.lastName}`).join(", ")}`}>
          <AutoModeIcon sx={{ marginLeft: 0.3, width: 20, height: 20 }} />
        </Tooltip>
      )}
      </Box>
    </Grid2>
  );

  if (!isDraggable && !isPatientView) return null;

  const visitSlotContent = (
    <div
      ref={isDraggable ? setDragElm : undefined}
      style={{
        borderRadius: '10px',
        border: '2px dashed #2E7D32',
        padding: '0.2rem 0.5rem',
        fontSize: "13px",
        width: 180,
        background: backgroundColor,
        opacity: isDraggable ? 1 : 0.5,
        cursor: isPatientView ? 'default' : 'grab',
      }}
    >
        <Badge sx={{ background: 'transparent', width: '100%' }} badgeContent={
          <>
            <IconButton
              onClick={isPatientView ? undefined : handleClick}
              disabled={isPatientView}
              sx={{
                background: 'white',
                width: 25,
                padding: 0,
                height: 25,
                '&:hover': {
                  background: 'white',
                },
              }}
            >
              <CancelTwoToneIcon sx={{ color: 'gray' }}  />
            </IconButton>

            {!isPatientView && (
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => {
                  cancelVisitRequest({
                    variables: {
                      taskId: data?.taskId
                    }
                  });
                  handleClose();
                  setCancelingVisitRequest(true);
                }}>
                  <ListItemText>Confirm Remove</ListItemText>
                </MenuItem>
              </Menu>
            )}
          </>
        }>
          {content}
        </Badge>
    </div>
  );

  return (
    cancelingVisitRequest ?
      <Skeleton animation="wave" variant="rounded" width="160px" height="60px" />
    :
    isPatientView ? (
      <Tooltip 
        title="Drag and drop is disabled in patient view. Switch to provider view to enable full drag-and-drop functionality."
        placement="top"
        arrow
      >
        {visitSlotContent}
      </Tooltip>
    ) : visitSlotContent
  );
};

export default VisitRequetSlot;

/**
 * Creates a visit_request event from a DB visit request
 * @param visitRequest The visit request
 * @returns A CustomMbscCalendarEventData object
 */
const createVisitRequestEventFromRequest = (
  visitRequest: LocalVisitRequest,
): CustomMbscCalendarEventData => {

  // either grab start and end time from visitResource or from DB visit resource
  const startTime = visitRequest.date;
  const endTime = addMinutes(startTime, visitRequest.duration);

  // Return adjusted event object
  return {
    type: "visit_request",
    id: visitRequest.id,
    referenceId: visitRequest.id,
    start: startTime.toISOString(),
    end: endTime.toISOString(),
    date: visitRequest.date.getTime(),
    startDate: startTime,
    endDate: endTime,
    dbDate: visitRequest.date.toISOString(),
    providerId: null,
    title: [visitRequest.patient?.firstName, visitRequest.patient?.lastName].join(" "),
    order: 1,
    dragBetweenResources: true,
    dragInTime: true,
  };
};

const CANCEL_VISIT_REQUEST_TASK = gql`
  mutation CancelVisitRequestTask(
    $taskId: Int!,
  )
  @permission(requires: createDragEvents)
  {
    cancelVisitRequest(
      taskId: $taskId
    ) {
      id
    }
  }
`;
